import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Inline } from '../atoms/Inline/Inline';
import { Heading } from '../atoms/Typography/Heading';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { useTranslation } from '../../i18n/useTranslation';
import {
  fetchMyOptInPrompts,
  MY_OPT_IN_PROMPTS_QUERY_KEY,
  submitOptInResponse,
  type MyOptInPrompt,
  type OptInResponse,
} from '../../services/optInPromptsService';
import { declinedAnswers, OptInPromptForm } from './OptInPromptForm';

/** Anchor of this section — the dialog's "Später" points here. */
export const OPT_IN_PROMPTS_ANCHOR = 'consents';

function PromptCard({ prompt }: { prompt: MyOptInPrompt }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // A fresh form after every save, built from the stored answer.
  const formKey = `${prompt.id}-${prompt.response?.createdAt ?? 'none'}`;

  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawFailed, setWithdrawFailed] = useState(false);

  const handleSaved = (response: OptInResponse): void => {
    queryClient.setQueryData<MyOptInPrompt[]>(MY_OPT_IN_PROMPTS_QUERY_KEY, (current) =>
      current?.map((p) => (p.id === prompt.id ? { ...p, due: false, response } : p)),
    );
    setSavedAt(response.createdAt);
  };

  async function withdraw(): Promise<void> {
    setWithdrawing(true);
    setWithdrawFailed(false);
    try {
      handleSaved(await submitOptInResponse(prompt.id, declinedAnswers(prompt), 'settings', i18n.resolvedLanguage ?? i18n.language));
    } catch {
      setWithdrawFailed(true);
    } finally {
      setWithdrawing(false);
    }
  }

  const answeredOn = prompt.response
    ? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(prompt.response.createdAt))
    : null;
  let status: string;
  if (!prompt.response) status = t('optInPrompts.common.statusOpen');
  else if (prompt.response.granted) status = t('optInPrompts.common.statusGranted', { date: answeredOn });
  else status = t('optInPrompts.common.statusDeclined', { date: answeredOn });

  return (
    <Card padding="lg">
      <Stack gap="4">
        <Stack gap="1">
          <Heading level={3}>{t(`optInPrompts.${prompt.id}.title`)}</Heading>
          <Text size="sm" color="secondary">{status}</Text>
        </Stack>
        <Text>{t(`optInPrompts.${prompt.id}.intro`)}</Text>
        {savedAt && (
          <LiveRegion>
            <Text size="sm">{t('optInPrompts.common.saved')}</Text>
          </LiveRegion>
        )}
        {prompt.active ? (
          <OptInPromptForm
            key={formKey}
            prompt={prompt}
            source="settings"
            onSaved={handleSaved}
            declineLabel={prompt.response?.granted ? t('optInPrompts.common.withdraw') : undefined}
          />
        ) : (
          // Switched off: no new consent, but an earlier one can still be withdrawn.
          <Stack gap="3">
            <Alert tone="info" live="off" compact>
              {t('optInPrompts.common.ended')}
            </Alert>
            {prompt.response?.granted && (
              <Inline justify="end">
                <Button variant="secondary" onClick={() => void withdraw()} isLoading={withdrawing}>
                  {t('optInPrompts.common.withdraw')}
                </Button>
              </Inline>
            )}
            {withdrawFailed && <Alert tone="danger">{t('optInPrompts.common.saveError')}</Alert>}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

export interface OptInPromptsSectionProps {
  className?: string;
}

/**
 * Settings → "Einwilligungen": every opt-in prompt that is on (or that the user
 * answered before) — give, change or withdraw the consents at any time. Nothing
 * is rendered while loading or when there is no such prompt.
 */
export function OptInPromptsSection({ className = '' }: OptInPromptsSectionProps) {
  const { t } = useTranslation();
  const prompts = useQuery({ queryKey: MY_OPT_IN_PROMPTS_QUERY_KEY, queryFn: fetchMyOptInPrompts, retry: false });

  // Nothing while loading or without a prompt: most of the time there is no campaign.
  if (prompts.isPending || (prompts.isSuccess && prompts.data.length === 0)) return null;

  const content = prompts.isError ? (
    <LoadErrorState
      compact
      titleLevel={3}
      title={t('optInPrompts.common.loadError')}
      onRetry={() => void prompts.refetch()}
      retryLabel={t('optInPrompts.common.retry')}
      retrying={prompts.isFetching}
    />
  ) : (
    <Stack gap="4">
      {prompts.data.map((prompt) => (
        <PromptCard key={prompt.id} prompt={prompt} />
      ))}
    </Stack>
  );

  return (
    <Section id={OPT_IN_PROMPTS_ANCHOR} title={t('optInPrompts.common.sectionTitle')} description={t('optInPrompts.common.sectionDescription')} className={className}>
      {content}
    </Section>
  );
}

export default OptInPromptsSection;
